import { Inject, Injectable } from '@angular/core';
import { DOCUMENT }           from '@angular/common';


export interface CookieOptions {
  domain?:   string;
  path?:     string;
  secure?:   boolean;
  sameSite?: 'Lax' | 'Strict' | 'None';
  expires?:  Date | number; // Date or days from now
}

/**
 * Thin cookie utility service backed by document.cookie directly.
 * Replaces ngx-cookie-service which has a bug where writes are silently dropped
 * in some browser/hostname combinations (e.g. localhost.* subdomains).
 */
@Injectable({ providedIn: 'root' })
export class CookieService {

  constructor(@Inject(DOCUMENT) private document: Document) {}

  check(name: string): boolean {
    return this.get(name) !== null;
  }

  get(name: string): string | null {
    const match = this.document.cookie.match(
      new RegExp('(?:^|; )' + encodeURIComponent(name) + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  set(name: string, value: string, options: CookieOptions = {}): void {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (options.expires !== undefined) {
      const date = typeof options.expires === 'number'
        ? new Date(Date.now() + options.expires * 864e5)
        : options.expires;
      cookie += `; expires=${date.toUTCString()}`;
    }

    cookie += `; path=${options.path ?? '/'}`;

    if (options.domain) cookie += `; domain=${options.domain}`;
    if (options.secure)  cookie += '; secure';

    cookie += `; sameSite=${options.sameSite ?? 'Lax'}`;

    this.document.cookie = cookie;
  }

  delete(name: string, options: CookieOptions = {}): void {
    this.set(name, '', { ...options, expires: new Date(0) });
  }
}
